import { GetCPGTransactionListData } from "@services/models/Transactions/GetCPGTransactionListModel";
import ConstantFunctions from "../../../../utils/constants_functions";
import { formatDisplayDate } from "../../../../utils/safeDate";
import {
  CpgPayeeDetails,
  CpgPaymentReceiptData,
  CpgReceiptDetail,
  CpgReceiptParty,
  CpgReceiptStatusTone,
} from "./types";

const constantFunctions = new ConstantFunctions();

const NETWORK_LABELS: Record<string, string> = {
  TRX: "TRON · TRC-20",
  ETH: "Ethereum · ERC-20",
  BSC: "BNB Smart Chain · BEP-20",
  MATIC: "Polygon",
};

const EXPLORER_LABELS: Record<string, string> = {
  TRX: "View on Tronscan",
  ETH: "View on Etherscan",
  BSC: "View on BscScan",
  MATIC: "View on Polygonscan",
};

const explorerUrl = (symbol: string, hash: string): string | undefined => {
  switch (symbol) {
    case "TRX":
      return `https://tronscan.org/#/transaction/${hash}`;
    case "ETH":
      return `https://etherscan.io/tx/${hash}`;
    case "BSC":
      return `https://bscscan.com/tx/${hash}`;
    case "MATIC":
      return `https://polygonscan.com/tx/${hash}`;
    default:
      return undefined;
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return asRecord(JSON.parse(trimmed));
    } catch {
      return null;
    }
  }
  if (typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
};

const readString = (
  record: Record<string, unknown>,
  keys: string[],
): string => {
  const entries = Object.entries(record);
  for (const key of keys) {
    const match = entries.find(
      ([entryKey]) => entryKey.toLowerCase() === key.toLowerCase(),
    );
    if (!match || match[1] == null) continue;
    const text = String(match[1]).trim();
    if (text) return text;
  }
  return "";
};

const titleCase = (value: string): string =>
  value.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

const formatDob = (value: string): string =>
  formatDisplayDate(value, "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

const formatReceiptDateTime = (value?: string | null): string => {
  if (!value) return "";
  const datePart = formatDisplayDate(value, "en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  const timePart = formatDisplayDate(value, "en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  if (datePart === value || timePart === value) return value;
  return `${datePart}, ${timePart}`;
};

const money = (amount: number, symbol?: string): string =>
  `${constantFunctions.formatAmount(amount)} ${symbol || ""}`.trim();

const statusTone = (status: string): CpgReceiptStatusTone => {
  const normalized = status.toLowerCase();
  if (
    normalized === "completed" ||
    normalized === "settled" ||
    normalized === "over payment"
  ) {
    return "completed";
  }
  if (normalized === "expired") return "expired";
  if (normalized === "pending" || normalized === "under payment") {
    return "pending";
  }
  return "neutral";
};

export const extractCpgPayeeDetails = (
  payload: unknown,
): CpgPayeeDetails | null => {
  const record = asRecord(payload);
  if (!record) return null;
  const payeeEntry = Object.entries(record).find(
    ([key]) => key.toLowerCase() === "payeedetails",
  );
  const payee = payeeEntry ? asRecord(payeeEntry[1]) : null;
  if (!payee) return null;

  const details: CpgPayeeDetails = {
    firstName: readString(payee, ["firstName"]),
    lastName: readString(payee, ["lastName"]),
    dob: readString(payee, ["DOB", "dob", "dateOfBirth"]),
    placeOfBirth: readString(payee, ["placeOfBirth"]),
    entityType: readString(payee, ["entityType", "type"]),
  };

  return Object.values(details).some(Boolean) ? details : null;
};

const payeeName = (payee: CpgPayeeDetails | null): string =>
  [payee?.firstName, payee?.lastName].filter(Boolean).join(" ").trim();

const payeeLines = (payee: CpgPayeeDetails | null): string[] => {
  if (!payee) return [];
  const lines: string[] = [];
  if (payee.entityType) lines.push(titleCase(payee.entityType));
  if (payee.dob) lines.push(`DOB ${formatDob(payee.dob)}`);
  if (payee.placeOfBirth) lines.push(titleCase(payee.placeOfBirth));
  return lines;
};

export const mapCpgTransactionToReceipt = (
  data: GetCPGTransactionListData,
  payee: CpgPayeeDetails | null,
): CpgPaymentReceiptData => {
  const isPayin =
    data.transactionType === "Merchant Payin"
      ? true
      : data.transactionType === "Merchant Payout"
        ? false
        : data.ledger !== "DR";

  const settledSymbol = data.tcs || "";
  const baseSymbol = data.fcs || "";
  const isCredit = data.ledger === "CR";
  const settledMovement = isCredit
    ? Number(data.settledAmountReceived ?? 0)
    : Number(data.settledAmountSent ?? 0);
  const heroAmount =
    settledMovement > 0
      ? settledMovement
      : Number(data.settledAmountRequested ?? 0);

  const name = payeeName(payee);
  const payeeTitle = name || "Payee";
  const merchantTitle = data.merchantName?.trim() || "Merchant";
  const payeeDetailLines = payeeLines(payee);
  const fromWallet = data.fromAddress || "";
  const toWallet = data.toAddress || data.systemAddress || "";

  const from: CpgReceiptParty = isPayin
    ? {
        title: payeeTitle,
        lines: payeeDetailLines,
        address: fromWallet,
        caption: "From wallet",
      }
    : {
        title: merchantTitle,
        lines: [...(name ? [`Payee ${name}`] : []), ...payeeDetailLines],
        address: fromWallet,
        caption: "From wallet",
      };

  const to: CpgReceiptParty = isPayin
    ? {
        title: merchantTitle,
        lines: [],
        address: toWallet,
        caption: "To wallet",
      }
    : {
        title: payeeTitle,
        lines: payeeDetailLines,
        address: toWallet,
        caption: "To wallet",
      };

  const networkSymbol = (data.networkSymbol || "").toUpperCase();
  const references: CpgReceiptDetail[] = [];
  if (data.orderId) {
    references.push({ label: "TYLT Order ID", value: data.orderId });
  }
  if (data.merchantOrderId) {
    references.push({
      label: "Merchant Order ID",
      value: data.merchantOrderId,
    });
  }
  if (data.transactionHash) {
    const href = explorerUrl(networkSymbol, data.transactionHash);
    references.push({
      label: "Transaction Hash",
      value: data.transactionHash,
      subValue: href ? EXPLORER_LABELS[networkSymbol] : undefined,
      href,
    });
  }

  const breakdown: CpgReceiptDetail[] = [
    { label: "Asset", value: settledSymbol || "—" },
    {
      label: "Network",
      value:
        NETWORK_LABELS[networkSymbol] ||
        data.networkName ||
        data.networkSymbol ||
        "—",
    },
    {
      label: "Requested in Base",
      value: money(Number(data.baseAmount ?? 0), baseSymbol),
    },
    {
      label: "Requested in Settled",
      value: money(Number(data.settledAmountRequested ?? 0), settledSymbol),
    },
    {
      label: "Amount transferred in base",
      value: money(Number(data.baseAmountReceived ?? 0), baseSymbol),
    },
    {
      label: "Amount transferred in settled",
      value: money(settledMovement, settledSymbol),
    },
  ];

  const receivedAt = formatReceiptDateTime(data.tdate);
  const timestamps: CpgReceiptDetail[] = [];
  if (receivedAt) {
    timestamps.push({ label: "Payment received at", value: receivedAt });
  }
  if (receivedAt && statusTone(data.status || "") === "completed") {
    timestamps.push({ label: "Payment completed at", value: receivedAt });
  }

  const status = data.status?.trim()
    ? constantFunctions.toSentenceCase(data.status)
    : "Pending";

  return {
    fileId: data.orderId || String(data.trxnId || "receipt"),
    status,
    statusTone: statusTone(data.status || ""),
    amountLabel: money(heroAmount, settledSymbol),
    networkLabel:
      NETWORK_LABELS[networkSymbol] ||
      [data.networkName, data.networkSymbol].filter(Boolean).join(" · "),
    from,
    to,
    references,
    breakdown,
    timestamps,
    merchantName: data.merchantName?.trim() || "",
    issuedAt: formatReceiptDateTime(new Date().toISOString()),
  };
};
